import { ChangeDetectionStrategy, Component } from '@angular/core';
import type { FormControl, UntypedFormControl, UntypedFormArray, FormGroup } from '@angular/forms';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { HoverEffectsFormGroup, MenuWidgetData } from '@web/widgets/menu/type';
import type { ESize } from '@common/enums';

import { EHorizontalPosition, EMenuLogoPosition, EFontStyle } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';

import { WebBaseControlSettingsComponent } from '@web/widgets/common/abstract/web-base-control-settings.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { WebHoverEffectsComponent } from '@web/shared/components/web-hover-effects/web-hover-effects.component';
import { WebHoverEffectsModule } from '@web/shared/components/web-hover-effects/web-hover-effects.module';

@Component({
    selector: 'web-style-settings',
    templateUrl: './style-settings.component.html',
    styleUrls: ['./style-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, WebColorPickerComponent, WebHoverEffectsModule],
})
export class StyleSettingsComponent extends WebBaseControlSettingsComponent<MenuWidgetData> {
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly EFontStyle = EFontStyle;
    public readonly FONT_SIZE_MAX = 72;
    public readonly EMenuLogoPosition = EMenuLogoPosition;
    public readonly EHorizontalPosition = EHorizontalPosition;

    protected initForm(): void {
        this.form = this.fb.group({
            isLogo: null,
            logoPosition: null,
            textColor: null,
            submenuTextColor: null,
            themeTextColor: null,
            menuItemIndent: null,
            horizontalPosition: null,
            layoutDirection: null,
            fontStile: this.fb.array([]),
            submenuFontStile: this.fb.array([]),
            fontSize: null,
            submenuFontSize: null,
            submenuListBackground: null,
            showIconOnMobile: null,
            iconColor: null,
            iconPosition: null,
            iconSize: null,
            hoverEffects: this.fb.group({
                type: null,
                color: null,
                transparency: null,
                lineType: null,
                lineWidth: null,
                animationType: null,
            }),
        });
    }
    protected setFormData(): void {
        this.fontStile.clear({ emitEvent: false });
        this.item.fontStile.forEach((item: EFontStyle) =>
            this.fontStile.push(this.fb.control(item), { emitEvent: false })
        );
        this.submenuFontStile.clear({ emitEvent: false });
        this.item.submenuFontStile.forEach((item: EFontStyle) =>
            this.submenuFontStile.push(this.fb.control(item), { emitEvent: false })
        );
        this.form.patchValue(this.item, { emitEvent: false });
    }

    public get isLogo(): FormControl<boolean> {
        return this.form.get('isLogo') as FormControl<boolean>;
    }

    public get logoPosition(): FormControl<EMenuLogoPosition> {
        return this.form.get('logoPosition') as FormControl<EMenuLogoPosition>;
    }

    public get horizontalPosition(): FormControl<EHorizontalPosition> {
        return this.form.get('horizontalPosition') as FormControl<EHorizontalPosition>;
    }

    public get fontSize(): UntypedFormControl {
        return this.form.get('fontSize') as UntypedFormControl;
    }

    public get submenuFontSize(): UntypedFormControl {
        return this.form.get('submenuFontSize') as UntypedFormControl;
    }

    public get layoutDirection(): UntypedFormControl {
        return this.form.get('layoutDirection') as UntypedFormControl;
    }

    public get textColor(): UntypedFormControl {
        return this.form.get('textColor') as UntypedFormControl;
    }

    public get submenuTextColor(): UntypedFormControl {
        return this.form.get('submenuTextColor') as UntypedFormControl;
    }

    public get themeTextColor(): UntypedFormControl {
        return this.form.get('themeTextColor') as UntypedFormControl;
    }

    public get menuItemIndent(): FormControl<ESize> {
        return this.form.get('menuItemIndent') as FormControl<ESize>;
    }

    public get fontStile(): UntypedFormArray {
        return this.form.get('fontStile') as UntypedFormArray;
    }

    public get submenuFontStile(): UntypedFormArray {
        return this.form.get('submenuFontStile') as UntypedFormArray;
    }

    public get submenuListBackground(): FormControl<string> {
        return this.form.get('submenuListBackground') as FormControl<string>;
    }

    public get showIconOnMobile(): FormControl<boolean> {
        return this.form.get('showIconOnMobile') as FormControl<boolean>;
    }

    public get iconColor(): FormControl<string> {
        return this.form.get('iconColor') as FormControl<string>;
    }

    public get iconPosition(): FormControl<EHorizontalPosition> {
        return this.form.get('iconPosition') as FormControl<EHorizontalPosition>;
    }

    public get iconSize(): FormControl<ESize> {
        return this.form.get('iconSize') as FormControl<ESize>;
    }

    public setlogoPosition(value: EMenuLogoPosition): void {
        this.logoPosition.setValue(value);

        if (value === EMenuLogoPosition.RIGHT) {
            this.horizontalPosition.setValue(EHorizontalPosition.L);
            this.iconPosition.setValue(EHorizontalPosition.L);
        } else if (value === EMenuLogoPosition.CENTER) {
            this.horizontalPosition.setValue(EHorizontalPosition.M);
            this.iconPosition.setValue(EHorizontalPosition.L);
        } else {
            this.horizontalPosition.setValue(EHorizontalPosition.R);
            this.iconPosition.setValue(EHorizontalPosition.R);
        }
    }

    public setHorizontalPosition(value: EHorizontalPosition): void {
        this.horizontalPosition.setValue(value);
    }

    public setFontSize(value: number): void {
        this.form.get('fontSize').patchValue(value);
    }

    public setSubmenuFontSize(value: number): void {
        this.form.get('submenuFontSize').patchValue(value);
    }

    public isActiveTextStyle(fontStyle: EFontStyle): boolean {
        return this.fontStile.value.includes(fontStyle);
    }

    public isActiveSubmenuTextStyle(fontStyle: EFontStyle): boolean {
        return this.submenuFontStile.value.includes(fontStyle);
    }

    public setMenuItemIndent(value: ESize): void {
        this.menuItemIndent.setValue(value);
    }

    public toggleActiveTextStyle(fontStyle: EFontStyle): void {
        const index = this.fontStile.value.findIndex((el) => el === fontStyle);

        if (index > -1) {
            this.fontStile.removeAt(index);
        } else {
            this.fontStile.push(this.fb.control(fontStyle));
        }
    }

    public toggleActiveSubmenuTextStyle(fontStyle: EFontStyle): void {
        const index = this.submenuFontStile.value.findIndex((el) => el === fontStyle);

        if (index > -1) {
            this.submenuFontStile.removeAt(index);
        } else {
            this.submenuFontStile.push(this.fb.control(fontStyle));
        }
    }

    public setTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.themeTextColor.setValue(presetName, { emitEvent: false });
        this.textColor.setValue(value);
    }

    public setSubmenuTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.themeTextColor.setValue(presetName, { emitEvent: false });
        this.submenuTextColor.setValue(value);
    }

    public setLayoutDirection(value: string): void {
        this.layoutDirection.setValue(value);
    }

    public toggleIconOnMobile(value: boolean): void {
        this.showIconOnMobile.setValue(value);
    }

    public setIconColor({ value, presetName }: ColorPickerEventPayload): void {
        this.themeTextColor.setValue(presetName, { emitEvent: false });
        this.iconColor.setValue(value);
    }

    public setSubmenuListBackground({ value, presetName }: ColorPickerEventPayload): void {
        this.themeTextColor.setValue(presetName, { emitEvent: false });
        this.submenuListBackground.setValue(value);
    }

    public setIconSize(value: ESize): void {
        this.iconSize.setValue(value);
    }

    public setIconPosition(value: EHorizontalPosition): void {
        this.iconPosition.setValue(value);
    }

    public get hoverEffects(): FormGroup<HoverEffectsFormGroup> {
        return this.form.get('hoverEffects') as FormGroup<HoverEffectsFormGroup>;
    }
}
